import { useState } from 'react'
import { partyApi, Participant, PartyStatus } from '../lib/api'

interface ParticipantListProps {
  participants: Participant[]
  partyId: string
  partyStatus: PartyStatus
  onAction: () => void
}

const statusBadge: Record<string, string> = {
  streaming: 'bg-green-100 text-green-700',
  connected: 'bg-blue-100 text-blue-700',
  paused: 'bg-orange-100 text-orange-700',
  disconnected: 'bg-gray-100 text-gray-500',
}

export default function ParticipantList({ participants, partyId, partyStatus, onAction }: ParticipantListProps) {
  const [loadingAction, setLoadingAction] = useState<string | null>(null)

  const partyIsLive = partyStatus === 'active' || partyStatus === 'paused'
  const partyNotEnded = partyStatus !== 'ended'

  const handleMuteToggle = async (p: Participant) => {
    const key = `mute-${p.user_uid}`
    setLoadingAction(key)
    const isMuted = p.status === 'paused'
    try {
      await partyApi.sendCommand(partyId, isMuted ? 'unmute' : 'mute', p.user_uid)
      onAction()
    } catch {
      // polling will catch up
    } finally {
      setLoadingAction(null)
    }
  }

  const handleKick = async (p: Participant) => {
    if (!confirm(`Kick "${p.device_name}"?`)) return
    const key = `kick-${p.user_uid}`
    setLoadingAction(key)
    try {
      await partyApi.sendCommand(partyId, 'kick', p.user_uid)
      onAction()
    } catch {
      // polling will catch up
    } finally {
      setLoadingAction(null)
    }
  }

  if (participants.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Participants (0)</h2>
        <p className="text-sm text-gray-500">No participants yet</p>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-3">
        Participants ({participants.length})
      </h2>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-gray-500">
              <th className="pb-2 pr-4 font-medium">Device</th>
              <th className="pb-2 pr-4 font-medium">Email</th>
              <th className="pb-2 pr-4 font-medium">Joined</th>
              <th className="pb-2 pr-4 font-medium">Status</th>
              <th className="pb-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {participants.map((p) => {
              const isAdmin = p.role === 'admin'
              const isDisconnected = p.status === 'disconnected'
              const canMute = partyIsLive && (p.status === 'streaming' || p.status === 'paused')
              const canKick = partyNotEnded && !isDisconnected
              const showActions = !isAdmin && (canMute || canKick)
              const isMuted = p.status === 'paused'

              return (
                <tr key={p.id} className={isDisconnected ? 'opacity-50' : ''}>
                  <td className="py-2 pr-4">
                    <span className="font-medium text-gray-900">{p.device_name}</span>
                  </td>
                  <td className="py-2 pr-4 text-gray-600 truncate max-w-[180px]">
                    {p.user_email}
                  </td>
                  <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">
                    {new Date(p.joined_at).toLocaleTimeString()}
                  </td>
                  <td className="py-2 pr-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge[p.status] ?? statusBadge.disconnected}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="py-2">
                    {isAdmin ? (
                      <span className="text-xs text-gray-400 font-medium">Admin</span>
                    ) : showActions ? (
                      <div className="flex items-center gap-2">
                        {canMute && (
                          <button
                            onClick={() => handleMuteToggle(p)}
                            disabled={loadingAction !== null}
                            className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            title={isMuted ? 'Unmute' : 'Mute'}
                          >
                            {loadingAction === `mute-${p.user_uid}` ? (
                              <Spinner />
                            ) : isMuted ? (
                              'Unmute'
                            ) : (
                              'Mute'
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleKick(p)}
                          disabled={loadingAction !== null}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50"
                          title="Kick"
                        >
                          {loadingAction === `kick-${p.user_uid}` ? <Spinner /> : 'Kick'}
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}
